// useNoncompliances.js
// ─────────────────────────────────────────────────────────────────────────────
// Custom hook that owns all data-fetching and derived state for the
// Noncompliances modal. noncompliances.js imports this hook and renders
// purely from what it returns - no fetch logic lives in the component.
//
// ── Why a dedicated hook? ──────────────────────────────────────────────────────
//
// The modal coordinates FOUR independent API calls:
//
// 1. fetchNoncompliances()            - seed: top box counts + default mod list
// 2. fetchNoncompliancesFullList()    - search corpus (fetched once on first search)
// 3. fetchNoncompliancesDetail()      - per-mod detail (fetched on each selection)
// 4. fetchNoncompliancesMetricTasks() - metric drill-down items (fetched on box click)
//
// Each call has its own loading/error lifecycle, and several derived states
// (filtered list, active metric rows) depend on combinations of them.
// Keeping this in the component would make it unwieldy; the hook provides a
// clean, testable boundary.
//
// ── Lazy-load strategy for fullList ───────────────────────────────────────────
//
// The full manufactured-items list can be large and is only needed when the
// user starts typing in the search box. We therefore defer fetching it until
// the first non-empty search query, then cache the result for the remainder of
// the modal session. Subsequent keystrokes filter in memory - no extra calls.
//
// ── Metric-tasks fetch strategy ───────────────────────────────────────────────
//
// Each metric box (completed / unevaluated / nonconformed) fetches its items
// on demand the first time the box is clicked. Results are cached in a ref map
// keyed by metric name so re-opening the same panel never re-fetches.
//
// ── AbortController cleanup ───────────────────────────────────────────────────
//
// Every fetch is tied to an AbortController whose signal is passed into the
// dataReceiver functions. The controller is aborted in the useEffect cleanup
// so in-flight requests don't update state after the component unmounts or a
// new fetch supersedes the previous one (detail fetch on rapid mod-clicks).
//
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchNoncompliances,
  fetchNoncompliancesDetail,
  fetchNoncompliancesFullList,
  fetchNoncompliancesMetricTasks,
} from "../../utils/dataReceiver";

// ─── State shape constants ────────────────────────────────────────────────────

/** @typedef {'idle'|'loading'|'success'|'error'} FetchStatus */

const IDLE = "idle";
const LOADING = "loading";
const SUCCESS = "success";
const ERROR = "error";

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useNoncompliances
 *
 * Call once at the top of the Noncompliances modal component.
 *
 * @returns {{
 *   // ── Seed (top boxes + default list) ─────────────────────────────────────
 *   seedStatus: FetchStatus,
 *   seedError: string | null,
 *   jobsCC: number,
 *   jobsUE: number,
 *   jobsNC: number,
 *
 *   // ── Left-panel list ──────────────────────────────────────────────────────
 *   listItems: Array<{ WoNum, ItemNum, BatchNum, dateLabel: string }>,
 *   listStatus: FetchStatus,
 *   listError: string | null,
 *
 *   // ── Search ───────────────────────────────────────────────────────────────
 *   search: string,
 *   setSearch: (val: string) => void,
 *   isSearching: boolean,
 *
 *   // ── Mod selection ────────────────────────────────────────────────────────
 *   selectedBatchNum: string | null,
 *   selectMod: (batchNum: string) => void,
 *
 *   // ── Detail panel ─────────────────────────────────────────────────────────
 *   detailStatus: FetchStatus,
 *   detailError: string | null,
 *   detailData: object | null,
 *
 *   // ── Station / task drill-down (UI convenience state) ─────────────────────
 *   selectedStationId: number | null,
 *   setSelectedStationId: (id: number | null) => void,
 *   selectedTaskId: string | null,
 *   setSelectedTaskId: (id: string | null) => void,
 *
 *   // ── Metric box drill-down ─────────────────────────────────────────────────
 *   activeMetric: string | null,
 *   setActiveMetric: (key: string | null) => void,
 *
 *   // ── Metric-tasks (populated when a metric box is open) ───────────────────
 *   metricTasksStatus: FetchStatus,
 *   metricTasksError: string | null,
 *   metricTasksItems: Array,   - items[] from fetchNoncompliancesMetricTasks
 * }}
 */
export function useNoncompliances() {
  // ── 1. Seed state ────────────────────────────────────────────────────────────
  const [seedStatus, setSeedStatus] = useState(LOADING);
  const [seedError, setSeedError] = useState(null);
  const [jobsCC, setJobsCC] = useState(0);
  const [jobsUE, setJobsUE] = useState(0);
  const [jobsNC, setJobsNC] = useState(0);
  const [onlineMods, setOnlineMods] = useState([]);

  // ── 2. Full-list cache (loaded lazily on first search) ───────────────────────
  const fullListCacheRef = useRef(null); // null = not yet fetched
  const [listStatus, setListStatus] = useState(IDLE);
  const [listError, setListError] = useState(null);

  // ── 3. Search ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");

  // ── 4. Selection ─────────────────────────────────────────────────────────────
  const [selectedBatchNum, setSelectedBatchNum] = useState(null);

  // ── 5. Detail state ──────────────────────────────────────────────────────────
  const [detailStatus, setDetailStatus] = useState(IDLE);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState(null);

  // ── 6. Sub-panel navigation ──────────────────────────────────────────────────
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);

  // ── 7. Metric-tasks drill-down ───────────────────────────────────────────────
  // Cache keyed by metric name so each panel only fetches once per session.
  const metricTasksCacheRef = useRef({}); // { completed: [], unevaluated: [], nonconformed: [] }
  const [metricTasksStatus, setMetricTasksStatus] = useState(IDLE);
  const [metricTasksError, setMetricTasksError] = useState(null);
  const [metricTasksItems, setMetricTasksItems] = useState([]);

  // ─── Fetch seed on mount ─────────────────────────────────────────────────────

  useEffect(() => {
    const controller = new AbortController();

    setSeedStatus(LOADING);
    setSeedError(null);

    fetchNoncompliances(controller.signal)
      .then((data) => {
        setJobsCC(data.jobsCC);
        setJobsUE(data.jobsUE);
        setJobsNC(data.jobsNC);
        setOnlineMods(data.onlineMods);
        setSeedStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setSeedError(err.message ?? "Failed to load noncompliance data.");
        setSeedStatus(ERROR);
      });

    return () => controller.abort();
  }, []);

  // ─── Fetch full list (lazily, on first search keystroke) ─────────────────────

  useEffect(() => {
    const q = search.trim();

    if (!q) {
      setListError(null);
      setListStatus(IDLE);
      return;
    }

    if (fullListCacheRef.current !== null) {
      setListStatus(SUCCESS);
      return;
    }

    const controller = new AbortController();
    setListStatus(LOADING);
    setListError(null);

    fetchNoncompliancesFullList(controller.signal)
      .then((rows) => {
        fullListCacheRef.current = rows;
        setListStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setListError(err.message ?? "Failed to load module list.");
        setListStatus(ERROR);
      });

    return () => controller.abort();
  }, [search]);

  // ─── Fetch detail when selection changes ─────────────────────────────────────

  useEffect(() => {
    if (!selectedBatchNum) {
      setDetailData(null);
      setDetailStatus(IDLE);
      setDetailError(null);
      setSelectedStationId(null);
      setSelectedTaskId(null);
      return;
    }

    const controller = new AbortController();
    setDetailStatus(LOADING);
    setDetailError(null);
    setDetailData(null);
    setSelectedStationId(null);
    setSelectedTaskId(null);

    fetchNoncompliancesDetail({ batchNum: selectedBatchNum }, controller.signal)
      .then((data) => {
        setDetailData(data);
        setDetailStatus(SUCCESS);
        const firstStation = data?.stations?.[0];
        if (firstStation) setSelectedStationId(firstStation.stationId);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setDetailError(err.message ?? "Failed to load module details.");
        setDetailStatus(ERROR);
      });

    return () => controller.abort();
  }, [selectedBatchNum]);

  // ─── Fetch metric-tasks when a metric box is opened ──────────────────────────
  //
  // Fires whenever activeMetric becomes non-null.
  // Results are cached by metric key - re-clicking the same box never re-fetches.
  // Closing the panel (activeMetric → null) leaves the cache intact so it
  // renders instantly if the user re-opens the same panel later in the session.

  useEffect(() => {
    if (!activeMetric) {
      // Panel closed - leave existing items visible until next open so there
      // is no flash of empty content if the user quickly re-opens.
      return;
    }

    // Already cached - hydrate state immediately, no fetch needed.
    if (metricTasksCacheRef.current[activeMetric] !== undefined) {
      setMetricTasksItems(metricTasksCacheRef.current[activeMetric]);
      setMetricTasksStatus(SUCCESS);
      setMetricTasksError(null);
      return;
    }

    // Not yet cached - fetch from the API.
    const controller = new AbortController();
    setMetricTasksStatus(LOADING);
    setMetricTasksError(null);
    setMetricTasksItems([]);

    fetchNoncompliancesMetricTasks({ metric: activeMetric }, controller.signal)
      .then((data) => {
        metricTasksCacheRef.current[activeMetric] = data.items;
        setMetricTasksItems(data.items);
        setMetricTasksStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setMetricTasksError(err.message ?? "Failed to load metric details.");
        setMetricTasksStatus(ERROR);
      });

    return () => controller.abort();
  }, [activeMetric]);

  // ─── Reset task selection when station changes ───────────────────────────────

  useEffect(() => {
    setSelectedTaskId(null);
  }, [selectedStationId]);

  // ─── Close metric drill-down when search is active ───────────────────────────

  useEffect(() => {
    if (search) setActiveMetric(null);
  }, [search]);

  // ─── Derive left-panel list items ────────────────────────────────────────────

  const listItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      return onlineMods.map((m) => ({
        WoNum: m.WoNum ?? "-",
        ItemNum: m.ItemNum ?? "-",
        BatchNum: m.BatchNum ?? "-",
        dateLabel: m.actualOnlineDate
          ? new Date(m.actualOnlineDate).toLocaleDateString()
          : (m.scheduledOnlineDate ?? "-"),
      }));
    }

    const source = fullListCacheRef.current ?? [];
    return source
      .filter(
        (m) =>
          (m.WoNum ?? "").toLowerCase().includes(q) ||
          (m.BatchNum ?? "").toLowerCase().includes(q) ||
          (m.ItemNum ?? "").toLowerCase().includes(q),
      )
      .map((m) => ({
        WoNum: m.WoNum ?? "-",
        ItemNum: m.ItemNum ?? "-",
        BatchNum: m.BatchNum ?? "-",
        dateLabel: m.CreatedAt
          ? new Date(m.CreatedAt).toLocaleDateString()
          : "-",
      }));
  }, [search, onlineMods]);

  // ─── selectMod callback ───────────────────────────────────────────────────────

  const selectMod = useCallback((batchNum) => {
    setSelectedBatchNum(batchNum);
    setActiveMetric(null);
  }, []);

  // ─── Return ───────────────────────────────────────────────────────────────────

  return {
    // Seed
    seedStatus,
    seedError,
    jobsCC,
    jobsUE,
    jobsNC,

    // List
    listItems,
    listStatus,
    listError,

    // Search
    search,
    setSearch,
    isSearching: listStatus === LOADING,

    // Selection
    selectedBatchNum,
    selectMod,

    // Detail
    detailStatus,
    detailError,
    detailData,

    // Sub-panel nav
    selectedStationId,
    setSelectedStationId,
    selectedTaskId,
    setSelectedTaskId,

    // Metric drill-down
    activeMetric,
    setActiveMetric,

    // Metric-tasks
    metricTasksStatus,
    metricTasksError,
    metricTasksItems,
  };
}
